import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from '@/i18n';
import { TRACKING_CASES } from './constants';

export type ProgressCircleProps = {
  icon?: ReactNode;
  text?: string | ReactNode;
  size?: number;
  trackColor?: string;
  backgroundColor?: string;
  strokeWidth?: number;
  outerRingColor?: string;
  outerRingWidth?: number;
  innerRingColor?: string;
  innerRingWidth?: number;
  selectedCase: string;
};

const clampPercent = (value: number) => {
  if (Number.isNaN(value)) {
    return 0;
  }

  return Math.min(Math.max(value, 0), 100);
};

const getProgressCircleContent = (
  selectedCase: string,
  isMobile: boolean = false,
  t: (key: string) => string
) => {
  const iconSize = isMobile ? 'h-16 w-16' : 'h-40 w-40';
  const textSize = isMobile ? 'text-base' : 'text-4xl';

  switch (selectedCase) {
    case TRACKING_CASES.registered:
      return {
        icon: (
          <img
            alt='registered'
            className={`${iconSize} object-contain`}
            src='/track/box.png'
          />
        ),
        percentColor: '#449486',
        trackColor: '#1e7165',
        percent: 100,
        text: null,
      };
    case TRACKING_CASES.arrivalAtDepot:
      return {
        icon: (
          <div className={`relative ${isMobile ? 'h-16 w-16' : 'h-40 w-40'}`}>
            <img
              alt='arrival at depot'
              className='h-full w-full object-contain'
              src='/track/box.png'
            />
            <svg
              className={`absolute ${
                isMobile
                  ? 'bottom-1.5 right-0 h-5 w-5'
                  : 'bottom-6 right-1 h-10 w-10'
              }`}
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden='true'
            >
              <circle
                cx='12'
                cy='12'
                r='10'
                stroke='#449486'
                strokeWidth='2'
                fill='#052633'
              />
              <path
                d='M8 12.5L10.5 15L16 9.5'
                stroke='#449486'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        ),
        percentColor: '#449486',
        trackColor: '#1e7165',
        percent: 100,
        text: null,
      };
    case TRACKING_CASES.departureFromDepot:
    case TRACKING_CASES.goingToLinkoping:
      return {
        percentColor: '#449486',
        trackColor: '#1e7165',
        percent: 100,
        text: null,
        icon: (
          <img
            alt='in transit'
            className={`${isMobile ? 'h-14 w-20' : 'h-32 w-44'} object-contain`}
            src='/track/departure-truck.png'
          />
        ),
      };
    case TRACKING_CASES.cancelled:
      return {
        icon: (
          <img
            alt='x'
            className={`${isMobile ? 'h-12 w-12' : 'h-30 w-30'} object-contain`}
            src='/track/x.png'
          />
        ),

        percentColor: '#DC3434',
        trackColor: '#DC3434',
        percent: 100,
        text: null,
      };
    case TRACKING_CASES.outForPickup:
      return {
        percentColor: '#22C70D',
        trackColor: '#B4C5C4',
        percent: 50,
        text: (
          <div className={`${textSize} font-semibold leading-tight text-white`}>
            {t('progress.outForPickupLine1')} <br />{' '}
            {t('progress.outForPickupLine2')}
          </div>
        ),
      };
    case TRACKING_CASES.intervalPickup:
      return {
        percentColor: '#22C70D',
        trackColor: '#DDE1DC',
        percent: 75,
        text: (
          <div className={`${textSize} font-semibold leading-tight text-white`}>
            {t('progress.arrivingSoonLine1')} <br />{' '}
            {t('progress.arrivingSoonLine2')}
          </div>
        ),
      };
    case TRACKING_CASES.intervalDelivery:
      return {
        percentColor: '#22C70D',
        trackColor: '#DDE1DC',
        percent: 75,
        text: (
          <div className={`${textSize} font-semibold leading-tight text-white`}>
            {t('progress.arrivingSoonLine1')} <br />{' '}
            {t('progress.arrivingSoonLine2')}
          </div>
        ),
      };
    case TRACKING_CASES.outForDelivery:
      return {
        percentColor: '#22C70D',
        trackColor: '#B4C5C4',
        percent: 50,
        text: (
          <div className={`${textSize} font-semibold leading-tight text-white`}>
            {t('progress.outForDeliveryLine1')} <br />{' '}
            {t('progress.outForDeliveryLine2')}
          </div>
        ),
      };
    case TRACKING_CASES.pickedUp:
      return {
        percentColor: '#22C70D',
        trackColor: '#22C70D',
        percent: 100,
        text: null,
        icon: (
          <svg
            className={isMobile ? 'h-12 w-12' : 'h-32 w-32'}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M5 13l4 4L19 7'
              stroke='#22C70D'
              strokeWidth='3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    case TRACKING_CASES.delivered:
      return {
        percentColor: '#22C70D',
        trackColor: '#22C70D',
        percent: 100,
        text: null,
        icon: (
          <svg
            className={isMobile ? 'h-12 w-12' : 'h-32 w-32'}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M5 13l4 4L19 7'
              stroke='#22C70D'
              strokeWidth='3'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        ),
      };
    case TRACKING_CASES.postponed:
      return {
        icon: (
          <div className={`relative ${isMobile ? 'h-16 w-16' : 'h-40 w-40'}`}>
            <div
              aria-hidden='true'
              className='h-full w-full bg-contain bg-center bg-no-repeat'
              style={{
                backgroundColor: '#DC3434',
                maskImage: 'url(/track/box.png)',
                WebkitMaskImage: 'url(/track/box.png)',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            />
            <svg
              className={`absolute ${
                isMobile
                  ? 'bottom-1.5 right-0 h-5 w-5'
                  : 'bottom-6 right-1 h-10 w-10'
              }`}
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              aria-hidden='true'
            >
              <circle
                cx='12'
                cy='12'
                r='10'
                stroke='#DC3434'
                strokeWidth='2'
                fill='#052633'
              />
              <path
                d='M12 7v5l3 2'
                stroke='#DC3434'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        ),
        percentColor: '#DC3434',
        trackColor: '#DC3434',
        percent: 100,
        text: null,
      };
    default:
      return {
        icon: null,
        percentColor: '#449486',
        trackColor: '#1e7165',
        percent: 0,
        text: null,
      };
  }
};
export const ProgressCircle = ({
  text,
  size = 320,

  backgroundColor = '#052633',
  strokeWidth = 16,
  outerRingColor = '#ffffff',
  outerRingWidth = 8,
  innerRingColor = '#ffffff',
  innerRingWidth = 8,
  selectedCase,
}: ProgressCircleProps) => {
  const isMobile = size < 250;
  const resolvedStrokeWidth = isMobile ? 10 : strokeWidth;
  const resolvedOuterRingWidth = isMobile ? 5 : outerRingWidth;
  const resolvedInnerRingWidth = isMobile ? 5 : innerRingWidth;
  const { t } = useTranslation();
  const content = getProgressCircleContent(selectedCase, isMobile, t);
  const normalizedPercent = content ? clampPercent(content.percent) : 0;
  const [animatedPercent, setAnimatedPercent] = useState(0);

  useEffect(() => {
    setAnimatedPercent(0);

    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => {
        setAnimatedPercent(normalizedPercent);
      });
    });

    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [normalizedPercent, selectedCase]);

  const halfSize = size / 2;
  const outerRingRadius = halfSize - resolvedOuterRingWidth / 2;
  const progressRadius =
    outerRingRadius - resolvedOuterRingWidth / 2 - resolvedStrokeWidth / 2;
  const innerRingRadius =
    progressRadius - resolvedStrokeWidth / 2 - resolvedInnerRingWidth / 2;
  const backgroundRadius = innerRingRadius - resolvedInnerRingWidth / 2;
  const safeOuterRingRadius = Math.max(outerRingRadius, 0);
  const safeProgressRadius = Math.max(progressRadius, 0);
  const safeInnerRingRadius = Math.max(innerRingRadius, 0);
  const safeBackgroundRadius = Math.max(backgroundRadius, 0);
  const progressCircumference = 2 * Math.PI * safeProgressRadius;
  const dashOffset =
    progressCircumference - (animatedPercent / 100) * progressCircumference;
  const backgroundDiameter = safeBackgroundRadius * 2;
  const textLines =
    typeof text === 'string'
      ? text.split('\n').map((line) => line.trim())
      : undefined;

  return (
    <div
      className='relative flex items-center justify-center drop-shadow-[0_18px_36px_rgba(9,45,54,0.28)]'
      style={{ height: size, width: size }}
    >
      <svg height={size} width={size}>
        {resolvedOuterRingWidth > 0 && safeOuterRingRadius > 0 && (
          <circle
            cx={halfSize}
            cy={halfSize}
            fill='transparent'
            r={safeOuterRingRadius}
            stroke={outerRingColor}
            strokeWidth={resolvedOuterRingWidth}
          />
        )}
        {resolvedStrokeWidth > 0 && safeProgressRadius > 0 && (
          <>
            <circle
              cx={halfSize}
              cy={halfSize}
              fill='transparent'
              r={safeProgressRadius}
              stroke={content?.trackColor}
              strokeLinecap='round'
              strokeWidth={resolvedStrokeWidth}
            />
            <circle
              cx={halfSize}
              cy={halfSize}
              fill='transparent'
              r={safeProgressRadius}
              stroke={content?.percentColor}
              strokeDasharray={progressCircumference}
              strokeDashoffset={dashOffset}
              strokeLinecap='round'
              strokeWidth={resolvedStrokeWidth}
              style={{
                transform: `rotate(-90deg)`,
                transformOrigin: '50% 50%',
                transition:
                  'stroke-dashoffset 1.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </>
        )}
        {resolvedInnerRingWidth > 0 && safeInnerRingRadius > 0 && (
          <circle
            cx={halfSize}
            cy={halfSize}
            fill='transparent'
            r={safeInnerRingRadius}
            stroke={innerRingColor}
            strokeWidth={resolvedInnerRingWidth}
          />
        )}
        {safeBackgroundRadius > 0 && (
          <circle
            cx={halfSize}
            cy={halfSize}
            fill={backgroundColor}
            r={safeBackgroundRadius}
          />
        )}
      </svg>
      <div
        className='absolute flex flex-col items-center justify-center rounded-full bg-transparent text-center text-white'
        style={{
          height: backgroundDiameter,
          width: backgroundDiameter,
        }}
      >
        {typeof content === 'object' && content ? (
          <>
            {content.icon && (
              <div className={isMobile ? 'mb-0' : 'mb-2 text-4xl'}>
                {content.icon}
              </div>
            )}
            {content?.text && (
              <div
                className={`font-semibold leading-tight text-white ${
                  isMobile ? 'text-base' : 'text-3xl'
                }`}
              >
                {content.text}
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};
